// frontend/src/screens/legal/PrivacyPolicyScreen.jsx
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Divider,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';

// Update this if your data-request inbox differs
const PRIVACY_CONTACT_EMAIL = 'singlescheme@beaumont.ie';

const Section = ({ number, title, children }) => (
  <Box sx={{ mb: 5 }}>
    <Typography
      variant="overline"
      sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: '0.08em' }}
    >
      {number}
    </Typography>
    <Typography
      variant="h6"
      sx={{ fontWeight: 700, mb: 2, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const PrivacyPolicyScreen = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ bgcolor: '#f0f4f8', minHeight: '100vh', py: { xs: 3, sm: 6 }, px: 2 }}>
      <Paper
        elevation={isMobile ? 0 : 3}
        sx={{
          maxWidth: 820,
          mx: 'auto',
          borderRadius: { xs: 2, sm: 3 },
          overflow: 'hidden',
        }}
      >
        {/* Header band, matching LoginScreen's style */}
        <Box
          sx={{
            bgcolor: 'primary.main',
            px: { xs: 2.5, sm: 4 },
            py: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Link
            component={RouterLink}
            to="/login"
            sx={{ color: 'white', display: 'flex', alignItems: 'center' }}
          >
            <ArrowBack fontSize="small" />
          </Link>
          <Box>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, lineHeight: 1.2 }}>
              Privacy &amp; Cookie Policy
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
              How we handle your data on this site
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: { xs: 2.5, sm: 4.5 } }}>
          {/* Cookie notice, called out up top */}
          <Alert severity="success" variant="outlined" sx={{ mb: 4 }}>
            This website uses only strictly necessary cookies, used only for the functioning of
            the website. There are no analytics or advertising cookies used. By continuing to
            browse the site, you are agreeing to our use of cookies.
          </Alert>

          <Section number="Part One" title="Website Privacy Policy">
            <Typography paragraph fontWeight={600}>1. Introduction</Typography>
            <Typography paragraph>
              We respect your right to privacy and comply with applicable data protection law.
              This policy outlines how we deal with any personal data you provide to us while
              using this website. If you are not happy with this policy, you should not use this
              website.
            </Typography>
            <Typography paragraph>
              By using this website, you are accepting the terms of this Privacy Policy. Any
              external links to other websites are clearly identifiable as such, and we are not
              responsible for the content or the privacy policies of those other websites.
            </Typography>

            <Typography paragraph fontWeight={600}>2. Information Collected</Typography>
            <Typography paragraph>We collect two types of information:</Typography>
            <Typography paragraph>
              <em>Personal Data</em> — data that identifies you or can be used to identify or
              contact you, such as your name, email address and telephone number. This is only
              collected if you voluntarily submit it to us (for example, when registering or
              signing in).
            </Typography>
            <Typography paragraph>
              <em>Non-Personal Data</em> — this website uses only strictly necessary cookies,
              used only for the functioning of the website. There are no analytics or advertising
              cookies used. Non-Personal Data comprises information that cannot be used to
              identify or contact you, such as clipped or anonymised IP addresses, browser types,
              and other anonymous statistical data about use of our website.
            </Typography>

            <Typography paragraph fontWeight={600}>3. Purposes</Typography>
            <Typography paragraph>
              Non-Personal Data is used solely for the functioning of the website. Any Personal
              Data you provide is processed to: authenticate your account and keep it secure,
              respond to communications you send us, and keep you informed of updates to our
              services.
            </Typography>

            <Typography paragraph fontWeight={600}>4. How Long Do We Keep Your Data?</Typography>
            <Typography paragraph>
              We keep your data only for as long as it is needed for the purpose it was collected,
              in line with applicable data protection requirements, and remove it once it is no
              longer needed.
            </Typography>

            <Typography paragraph fontWeight={600}>
              5. Updating, Verifying and Deleting Personal Data
            </Typography>
            <Typography paragraph>
              You may inform us of any changes to your Personal Data, and we will update or delete
              your Personal Data accordingly, in line with applicable data protection law.
            </Typography>

            
          </Section>

          <Section number="Part Two" title="Cookie Policy">
            <Typography paragraph fontWeight={600}>About Cookies</Typography>
            <Typography paragraph>
              Cookies are small text files which are downloaded to your computer or mobile device
              when you visit a website. Your web browser then sends these cookies back to the
              website on each subsequent visit so it can recognise you and remember things like
              user preferences.
            </Typography>

            <Typography paragraph fontWeight={600}>How We Use Cookies</Typography>
            <Typography paragraph>
              This website uses only strictly necessary cookies, used only for the functioning of
              the website. There are no analytics or advertising cookies used. By continuing to
              browse the site, you are agreeing to our use of cookies.
            </Typography>

            <TableContainer sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Cookie type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Used?</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Purpose</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Strictly necessary</TableCell>
                    <TableCell sx={{ color: 'success.main', fontWeight: 700 }}>Yes</TableCell>
                    <TableCell>
                      Enables core site functionality (e.g. sign-in, session security, CSRF
                      protection). The site cannot work properly without these.
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Analytics / performance</TableCell>
                    <TableCell sx={{ color: 'error.main', fontWeight: 700 }}>No</TableCell>
                    <TableCell>Not used — we do not track how visitors use this site.</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Advertising / targeting</TableCell>
                    <TableCell sx={{ color: 'error.main', fontWeight: 700 }}>No</TableCell>
                    <TableCell>Not used — we do not serve ads or build advertising profiles.</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <Typography paragraph fontWeight={600}>Managing Cookies</Typography>
            <Typography paragraph sx={{ mb: 0 }}>
              You can restrict or block cookies through your browser settings. Restricting cookies
              may change the way the website functions. To find out more about cookies, visit{' '}
              <Link href="https://www.allaboutcookies.org" target="_blank" rel="noopener">
                www.allaboutcookies.org
              </Link>
              .
            </Typography>
          </Section>

          <Divider sx={{ mb: 4 }} />

          <Section number="Part Three" title="Update, Verify or Delete Your Data">
            <Typography paragraph sx={{ mb: 0 }}>
              To have your Personal Data updated, amended or
              removed, please email us at{' '}
              <Link href={`mailto:${PRIVACY_CONTACT_EMAIL}`}>{PRIVACY_CONTACT_EMAIL}</Link>.
            </Typography>
          </Section>

          <Typography variant="caption" color="text.secondary">
            Questions about this policy can be directed to{' '}
            <Link href={`mailto:${PRIVACY_CONTACT_EMAIL}`}>{PRIVACY_CONTACT_EMAIL}</Link>.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default PrivacyPolicyScreen;